import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BellRing,
  ChevronDown,
  RefreshCw,
  ShieldCheck,
  UserRound,
  UsersRound,
} from "lucide-react";
import api from "../../lib/Urls";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "Unknown";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "Unknown"
    : date.toLocaleString();
};

const isActiveUser = (user) =>
  String(user?.signInFlag || "").toLowerCase() === "online";

const AdminUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialScope =
    location.state?.initialScope === "active" ? "active" : "all";

  const [scope, setScope] = useState(initialScope);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextPage, setNextPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // Keeps the newest request authoritative if the user switches tabs quickly.
  const requestVersion = useRef(0);

  const loadPage = useCallback(
    async (
      targetScope,
      page,
      {
        append = false,
        refresh = false,
      } = {}
    ) => {
      const version = ++requestVersion.current;

      if (append) {
        setLoadingMore(true);
      } else if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        setError("");

        const res = await api.get("/api/admin/users/page", {
          params: {
            scope: targetScope,
            page,
            size: PAGE_SIZE,
          },
        });

        if (version !== requestVersion.current) return;

        const responseUsers = Array.isArray(res.data?.users)
          ? res.data.users
          : [];

        const responseTotal = Number(res.data?.total);
        const responseNextPage = Number(res.data?.nextPage);

        setUsers((current) => {
          if (!append) return responseUsers;

          const byId = new Map(
            current.map((user) => [user.userId, user])
          );

          responseUsers.forEach((user) => {
            byId.set(user.userId, user);
          });

          return Array.from(byId.values());
        });

        setTotal(
          Number.isFinite(responseTotal)
            ? responseTotal
            : responseUsers.length
        );

        setNextPage(
          Number.isInteger(responseNextPage)
            ? responseNextPage
            : page + 1
        );

        setHasMore(Boolean(res.data?.hasMore));
      } catch (requestError) {
        if (version !== requestVersion.current) return;

        if (
          requestError.response?.status === 401 ||
          requestError.response?.status === 403
        ) {
          navigate("/", { replace: true });
          return;
        }

        if (!append) {
          setUsers([]);
          setTotal(0);
          setNextPage(0);
          setHasMore(false);
        }

        setError(
          targetScope === "all"
            ? "Unable to load registered users."
            : "Unable to load users with valid sign-in tokens."
        );

        console.error(
          `Failed to load ${targetScope} users`,
          requestError
        );
      } finally {
        if (version === requestVersion.current) {
          setLoading(false);
          setLoadingMore(false);
          setRefreshing(false);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    setUsers([]);
    setTotal(0);
    setNextPage(0);
    setHasMore(false);
    setError("");
    setLoading(true);

    loadPage(scope, 0);
  }, [scope, loadPage]);

  const changeScope = (nextScope) => {
    if (
      nextScope === scope ||
      loading ||
      loadingMore ||
      refreshing
    ) {
      return;
    }

    setScope(nextScope);
  };

  const refreshCurrentScope = () => {
    if (loading || loadingMore || refreshing) return;

    setUsers([]);
    setTotal(0);
    setNextPage(0);
    setHasMore(false);

    loadPage(scope, 0, { refresh: true });
  };

  const seeMore = () => {
    if (
      !hasMore ||
      loading ||
      loadingMore ||
      refreshing
    ) {
      return;
    }

    loadPage(scope, nextPage, { append: true });
  };

  const title =
    scope === "active" ? "Active Users" : "All Users";

  const description =
    scope === "active"
      ? "Users whose stored sign-in tokens are currently valid."
      : "Every registered DealSwapify account in the database.";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="container mx-auto flex-1 px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card hover:bg-muted"
                aria-label="Back to home"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>

              <div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />

                  <h1 className="font-heading text-2xl font-bold">
                    User Management
                  </h1>
                </div>

                <p className="mt-1 text-sm text-muted-foreground">
                  Load users in batches of 10.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/admin/notices"
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground"
              >
                <BellRing className="h-4 w-4" />
                Post Notice
              </Link>

              <button
                type="button"
                onClick={refreshCurrentScope}
                disabled={loading || loadingMore || refreshing}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    refreshing ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </button>
            </div>
          </div>

          <div className="mb-4 inline-flex rounded-xl border border-border bg-card p-1">
            <button
              type="button"
              onClick={() => changeScope("active")}
              disabled={loading || loadingMore || refreshing}
              className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
                scope === "active"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <UserRound className="h-4 w-4" />
              Active
            </button>

            <button
              type="button"
              onClick={() => changeScope("all")}
              disabled={loading || loadingMore || refreshing}
              className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
                scope === "all"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <UsersRound className="h-4 w-4" />
              All
            </button>
          </div>

          <div className="mb-4 rounded-xl border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">
              {title}
            </div>

            <div className="mt-1 text-3xl font-bold text-primary">
              {loading ? "—" : total}
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              {description}
            </p>

            {!loading && total > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Showing {users.length} of {total}
              </p>
            )}
          </div>

          {refreshing && !loading && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              Refreshing {scope === "active" ? "active" : "all"} users...
            </div>
          )}

          {error && !loading && (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-border bg-card p-10">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <div
                  className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin"
                  aria-hidden="true"
                />

                <div>
                  <p className="text-sm font-medium text-foreground">
                    Loading {scope === "active" ? "active" : "all"} users...
                  </p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    Fetching the first {PAGE_SIZE} users.
                  </p>
                </div>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-8 text-center">
              <UserRound className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />

              <p className="text-sm font-medium">
                {scope === "active"
                  ? "No active users found."
                  : "No registered users found."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                {users.map((user) => {
                  const active = isActiveUser(user);

                  return (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() =>
                        navigate(`/admin/users/${user.userId}`)
                      }
                      className="flex w-full items-center gap-3 border-b border-border px-4 py-4 text-left transition hover:bg-muted/50 last:border-b-0"
                    >
                      <img
                        src={
                          user.profileImageUrl ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.username || "User"
                          )}&background=random&color=fff&size=96`
                        }
                        alt=""
                        className="h-11 w-11 rounded-full border border-border object-cover"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-semibold">
                            {user.username || "Unnamed user"}
                          </span>

                          {String(user.role || "").toLowerCase() ===
                            "admin" && (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                              Admin
                            </span>
                          )}
                        </div>

                        <p className="truncate text-xs text-muted-foreground">
                          {user.email}
                        </p>

                        <p className="mt-1 truncate text-xs text-muted-foreground">
                          {[user.location, user.city, user.country]
                            .filter(Boolean)
                            .join(", ") || "Location not set"}
                        </p>
                      </div>

                      <div className="hidden shrink-0 text-right sm:block">
                        <div
                          className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                            active
                              ? "text-primary"
                              : "text-muted-foreground"
                          }`}
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${
                              active
                                ? "bg-primary"
                                : "bg-muted-foreground/50"
                            }`}
                          />

                          {active ? "Active" : "Offline"}
                        </div>

                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {active ? "Signed in " : "Last sign-in "}
                          {formatDate(user.signInTime)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {hasMore && (
                <div className="mt-5 flex justify-center">
                  <button
                    type="button"
                    onClick={seeMore}
                    disabled={loadingMore}
                    className="inline-flex min-w-36 items-center justify-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loadingMore ? (
                      <>
                        <div className="h-4 w-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        See more
                      </>
                    )}
                  </button>
                </div>
              )}

              {!hasMore && users.length > PAGE_SIZE && (
                <p className="mt-4 text-center text-xs text-muted-foreground">
                  All {total} users loaded.
                </p>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminUsers;
